import { useState } from "react";
import axios from "axios";
import IconButton from "@mui/material/IconButton";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import { Box, Button, TextField, Snackbar, Alert } from "@mui/material";
import { Formik } from "formik";
import * as yup from "yup";
import useMediaQuery from "@mui/material/useMediaQuery";
import Header from "../../components/Header";
import { useAuth } from "../../hooks/useAuth";
import { useNavigate } from "react-router-dom";
import { Typography } from "@mui/material";
import { MAIN_URL } from "../../data/const";

const DriverForm = () => {
  const { user, api } = useAuth();
  const navigate = useNavigate();
  const isNonMobile = useMediaQuery("(min-width:600px)");
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [notification, setNotification] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const handleFormSubmit = async (values, { resetForm }) => {
    if (!user?._id) {
      setNotification({
        open: true,
        message: "Authentication error. Please login again.",
        severity: "error",
      });
      return;
    }

    setIsLoading(true);
    try {
      const formData = new FormData();
      formData.append("username", values.username);
      formData.append("email", values.email);
      formData.append("phone", values.phone);
      formData.append("age", values.age);
      formData.append("password", values.password);
      formData.append("ownId", user._id);

      if (values.image?.[0]) {
        formData.append("image", values.image[0]);
      }

      if (values.legal?.[0]) {
        formData.append("legal", values.legal[0]);
      }

      // Make API request using the authenticated api instance
      const response = await axios.post(
        `${MAIN_URL}/api/v1/driver/registerAPI`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );
      console.log(response.data);
      setNotification({
        open: true,
        message: "Driver added successfully!",
        severity: "success",
      });

      // Reset form after successful submission
      resetForm();

      // Redirect to drivers list after short delay
      setTimeout(() => {
        navigate("/team");
      }, 2000);
    } catch (error) {
      console.error("Driver registration error:", error);

      setNotification({
        open: true,
        message:
          error.response?.data?.message ||
          "Failed to add driver. Please try again.",
        severity: "error",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleCloseNotification = () => {
    setNotification((prev) => ({ ...prev, open: false }));
  };

  return (
    <Box m="20px">
      <Header title="ADD DRIVER" subtitle="Create a new Driver Profile" />

      <Formik
        onSubmit={handleFormSubmit}
        initialValues={initialValues}
        validationSchema={checkoutSchema}
      >
        {({
          values,
          errors,
          touched,
          handleBlur,
          handleChange,
          handleSubmit,
          setFieldValue,
        }) => (
          <form onSubmit={handleSubmit}>
            <Box
              display="grid"
              gap="30px"
              gridTemplateColumns="repeat(4, minmax(0, 1fr))"
              sx={{
                "& > div": { gridColumn: isNonMobile ? undefined : "span 4" },
              }}
            >
              <Snackbar
                open={notification.open}
                autoHideDuration={6000}
                onClose={handleCloseNotification}
                anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
              >
                <Alert
                  onClose={handleCloseNotification}
                  severity={notification.severity}
                  elevation={6}
                  variant="filled"
                >
                  {notification.message}
                </Alert>
              </Snackbar>

              <TextField
                fullWidth
                variant="filled"
                type="text"
                label="Driver Name"
                onBlur={handleBlur}
                onChange={handleChange}
                value={values.username}
                name="username"
                error={!!touched.username && !!errors.username}
                helperText={touched.username && errors.username}
                sx={{ gridColumn: "span 2" }}
              />

              <TextField
                fullWidth
                variant="filled"
                type="text"
                label="Email"
                onBlur={handleBlur}
                onChange={handleChange}
                value={values.email}
                name="email"
                error={!!touched.email && !!errors.email}
                helperText={touched.email && errors.email}
                sx={{ gridColumn: "span 4" }}
              />

              <TextField
                fullWidth
                variant="filled"
                type="text"
                label="Phone Number"
                onBlur={handleBlur}
                onChange={handleChange}
                value={values.phone}
                name="phone"
                error={!!touched.phone && !!errors.phone}
                helperText={touched.phone && errors.phone}
                sx={{ gridColumn: "span 4" }}
              />

              <TextField
                fullWidth
                variant="filled"
                type="number"
                label="Age"
                onBlur={handleBlur}
                onChange={handleChange}
                value={values.age}
                name="age"
                error={!!touched.age && !!errors.age}
                helperText={touched.age && errors.age}
                sx={{ gridColumn: "span 4" }}
              />

              <TextField
                fullWidth
                variant="filled"
                type={showPassword ? "text" : "password"}
                label="Password"
                onBlur={handleBlur}
                onChange={handleChange}
                value={values.password}
                name="password"
                error={!!touched.password && !!errors.password}
                helperText={touched.password && errors.password}
                sx={{ gridColumn: "span 4" }}
                InputProps={{
                  endAdornment: (
                    <IconButton
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                    >
                      {showPassword ? (
                        <VisibilityOffIcon />
                      ) : (
                        <VisibilityIcon />
                      )}
                    </IconButton>
                  ),
                }}
              />

              <Box sx={{ gridColumn: "span 4" }}>
                <input
                  type="file"
                  accept="image/*"
                  name="image"
                  onChange={(event) => {
                    setFieldValue("image", event.currentTarget.files);
                  }}
                  style={{ display: "none" }}
                  id="image-upload"
                />
                <label htmlFor="image-upload">
                  <Button
                    variant="contained"
                    component="span"
                    fullWidth
                    sx={{ mb: 1 }}
                  >
                    Upload Profile Image
                  </Button>
                </label>
                {values.image && values.image[0] && (
                  <Typography variant="body2">
                    Selected: {values.image[0].name}
                  </Typography>
                )}
                {touched.image && errors.image && (
                  <Typography color="error" variant="caption">
                    {errors.image}
                  </Typography>
                )}
              </Box>

              <Box sx={{ gridColumn: "span 4" }}>
                <input
                  type="file"
                  accept=".pdf,.doc,.docx,image/*"
                  name="legal"
                  onChange={(event) => {
                    setFieldValue("legal", event.currentTarget.files);
                  }}
                  style={{ display: "none" }}
                  id="legal-upload"
                />
                <label htmlFor="legal-upload">
                  <Button
                    variant="contained"
                    component="span"
                    fullWidth
                    sx={{ mb: 1 }}
                  >
                    Upload Legal Documents
                  </Button>
                </label>
                {values.legal && values.legal[0] && (
                  <Typography variant="body2">
                    Selected: {values.legal[0].name}
                  </Typography>
                )}
                {touched.legal && errors.legal && (
                  <Typography color="error" variant="caption">
                    {errors.legal}
                  </Typography>
                )}
              </Box>
            </Box>

            <Box display="flex" justifyContent="end" mt="20px">
              <Button
                type="submit"
                color="secondary"
                variant="contained"
                disabled={isLoading}
              >
                {isLoading ? "Adding Driver..." : "Add New Driver"}
              </Button>
            </Box>
          </form>
        )}
      </Formik>
    </Box>
  );
};

const phoneRegExp =
  /^((\+[1-9]{1,4}[ -]?)|(\([0-9]{2,3}\)[ -]?)|([0-9]{2,4})[ -]?)*?[0-9]{3,4}[ -]?[0-9]{3,4}$/;

const checkoutSchema = yup.object().shape({
  username: yup
    .string()
    .trim()
    .required("Username is required")
    .matches(/^\S*$/, "Username should not contain spaces"),
  email: yup.string().email("Invalid email").required("Email is required"),
  phone: yup
    .string()
    .matches(phoneRegExp, "Invalid phone number")
    .required("Phone number is required"),
  age: yup
    .number()
    .positive("Age must be a positive number")
    .required("Age is required"),
  password: yup
    .string()
    .required("Password is required")
    .min(6, "Password must be at least 6 characters"),
  image: yup
    .mixed()
    .test(
      "fileRequired",
      "Profile image is required",
      (value) => value && value.length > 0
    ),
  legal: yup
    .mixed()
    .test(
      "fileRequired",
      "Legal document is required",
      (value) => value && value.length > 0
    ),
});

const initialValues = {
  username: "",
  email: "",
  phone: "",
  age: "",
  password: "",
  image: null,
  legal: null,
};

export default DriverForm;
